import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, Link, useLocation, useNavigate } from "react-router-dom";
import { logoutUser } from "./user/userSlice";

const styles = {
  navLink: {
    color: "#605943", // Changed text color
    textDecoration: "none",
    fontSize: "16px",
    fontFamily: "Unbounded, sans-serif",
    fontWeight: 400,
    "&:hover": {
      backgroundColor: "transparent", // Removed background color
      color: "#1875D1", // Keep hover color as is
      borderRadius: "0px",
    },
  },
  activeNavLink: {
    borderBottom: "1px solid #FFC249",
    paddingBottom: "0px",
    marginTop: "-3px",
    height: "100%",
    color: "#FF0000",
  },
  mobileNavItem: {
    color: "#605943",
    textDecoration: "none",
    fontSize: "16px",
    fontFamily: "Unbounded, sans-serif",
    fontWeight: 400,
    width: "100%",
    justifyContent: "flex-start",
    padding: "12px 16px",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#FF0000", // Changed highlight color to red
    },
  },
  mobileActiveNavItem: {
    color: "#FF0000",
  },
};

const normalizeUrl = (url) => {
  // Remove trailing slash if it exists
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

const getNavLinkStyle = (isActive) => ({
  my: 2,
  display: "block",
  textAlign: "center",
  textTransform: "none",
  backgroundColor: "transparent", // Removed background color
  borderRadius: "0px",
  "&:focus": {
    outline: "none",
  },
  ...styles.navLink,
  ...(isActive ? styles.activeNavLink : {}),
});

const pages = [];
const settings = [
  { name: "Account", link: "/account" },
  { name: "Logout", link: "/logout" },
];

export default function NavBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const userFullName = useSelector((state) => state.user.fullName);

  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const getPageUrl = (page) => {
    if (page === "IRL") {
      return "";
    } else if (page === "Profile") {
      return generatePath("/profile");
    } else if (page === "Home") {
      return "/";
    } else {
      return null;
    }
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "transparent",
        boxShadow: "none",
        paddingTop: 1,
        margin: "0 auto",
        maxWidth: "1200px",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          width: "100%",
          paddingLeft: "8px !important",
          paddingRight: "8px !important",
          "&.MuiToolbar-root": {
            paddingLeft: "8px",
            paddingRight: "8px",
          },
          backgroundColor: "#000000",
        }}
      >
        {/* Left side - Logo and Title */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 16,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              marginRight: 1,
            }}
          >
            <Box
              component="img"
              src="/static/frontend/images/favicon.png"
              sx={{
                width: 24,
                height: 24,
                borderRadius: 12,
              }}
            />
          </Box>

          <Typography
            variant={isMobile ? "h6" : "h4"}
            noWrap
            component={Link}
            to="/"
            sx={{
              fontWeight: 400,
              color: "inherit",
              textDecoration: "none",
              fontSize: { xs: "1rem", md: "1.5rem" },
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Box component="span" sx={{ color: "#F53D3B" }}>
              smooth
            </Box>
            <Box component="span" sx={{ color: "#FFFFFF" }}>
              speak
            </Box>
          </Typography>
        </Box>

        {/* Right side - Learn More or Menu */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {!isAuthenticated ? (
            <Button
              component="a"
              href="https://smoothspeak.ai"
              sx={{
                color: "#F53D3B",
                borderColor: "#F53D3B",
                fontSize: { xs: "0.75rem", md: "1rem" },
                "&:hover": {
                  borderColor: "#F53D3B",
                  backgroundColor: "rgba(245, 61, 59, 0.04)",
                },
              }}
              variant="outlined"
            >
              Learn More
            </Button>
          ) : (
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                border: { xs: "none", md: "2px solid #F53D3B" },
                color: "#F53D3B",
                "&:hover": {
                  backgroundColor: "rgba(245, 61, 59, 0.04)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>

      {isAuthenticated && (
        <>
          {/* Desktop menu */}
          <Box sx={{ display: { xs: "none", md: "flex" }, mr: 2 }}>
            {pages.map((page) => (
              <div key={page.name}>
                <Button
                  onClick={() => navigate(page.link)}
                  sx={getNavLinkStyle(
                    normalizeUrl(location.pathname) === getPageUrl(page.name)
                  )}
                >
                  {page.name}
                </Button>
              </div>
            ))}
          </Box>

          {/* Desktop avatar and menu */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexShrink: 0,
            }}
          >
            <Menu
              sx={{
                mt: "45px",
                "& .MuiPaper-root": {
                  backgroundColor: "#1C1C1E",
                },
                "& .MuiMenuItem-root": {
                  color: "#FFFFFF",
                },
              }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem
                  key={setting.name}
                  onClick={() => {
                    if (setting.name === "Account") {
                      navigate(setting.link);
                    } else if (setting.name === "Logout") {
                      dispatch(logoutUser());
                    }
                    handleCloseUserMenu();
                  }}
                >
                  <Typography textAlign="center">{setting.name}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Mobile menu drawer */}
          <Drawer
            anchor="top"
            open={mobileMenuOpen}
            onClose={handleMobileMenuClose}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                padding: "16px",
                backgroundColor: "#1C1C1E",
              }}
            >
              <List>
                {settings.map((item) => (
                  <ListItem key={item.name} disablePadding>
                    <Button
                      fullWidth
                      onClick={() => {
                        handleMobileMenuClose();
                        if (item.name === "Logout") {
                          dispatch(logoutUser());
                        } else {
                          navigate(item.link);
                        }
                      }}
                      sx={{
                        ...styles.mobileNavItem,
                        color:
                          location.pathname === item.link
                            ? "#FF3D3B"
                            : "#FFFFFF",
                        ...(location.pathname === item.link
                          ? styles.mobileActiveNavItem
                          : {}),
                      }}
                    >
                      {item.name}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Drawer>
        </>
      )}
    </AppBar>
  );
}
